import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography, Card } from "antd";
import { ShopOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { PublicLayout } from "../shared/components/layout";
import { useAuth, Restaurant } from "../shared/contexts";
import { useAntdTheme } from "../shared/hooks";
import theme from "../config/theme";

const { Title, Text } = Typography;

interface LocationState {
  from?: {
    pathname: string;
  };
}

const RestaurantSelection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { authState, selectRestaurant, logout } = useAuth();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Redirect to login if not authenticated or no restaurants
  useEffect(() => {
    if (!authState.isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }
    if (authState.restaurants.length === 0) {
      navigate("/login", { replace: true });
      return;
    }
    // If only one restaurant, auto-select and redirect
    if (authState.restaurants.length === 1) {
      handleSelectRestaurant(authState.restaurants[0]);
    }
  }, [authState.isAuthenticated, authState.restaurants]);

  const handleSelectRestaurant = (restaurant: Restaurant) => {
    selectRestaurant(restaurant.id);

    // Navigate to intended destination or home
    const state = location.state as LocationState | null;
    const from = state?.from?.pathname || "/";
    navigate(from, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent, restaurant: Restaurant) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelectRestaurant(restaurant);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Don't render if redirecting
  if (!authState.isAuthenticated || authState.restaurants.length <= 1) {
    return null;
  }

  return (
    <PublicLayout>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: token.colorPrimary,
          padding: token.paddingLG,
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: "500px",
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadow,
          }}
          styles={{
            body: {
              padding: token.paddingXL,
            },
          }}
        >
          <div style={{ textAlign: "center", marginBottom: token.paddingXL }}>
            <ShopOutlined
              style={{
                fontSize: theme.typography.fontSize["3xl"],
                color: token.colorPrimary,
                marginBottom: theme.spacing.sm,
              }}
            />
            <Title
              level={2}
              style={{
                color: token.colorPrimary,
                marginBottom: token.paddingXS,
                marginTop: 0,
              }}
            >
              {t("select_restaurant")}
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: token.fontSize,
              }}
            >
              {t("select_restaurant_description")}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing.md,
            }}
          >
            {authState.restaurants.map((restaurant) => {
              const isSelected = authState.currentRestaurant?.id === restaurant.id;
              const isHovered = hoveredId === restaurant.id;

              return (
                <button
                  key={restaurant.id}
                  onClick={() => handleSelectRestaurant(restaurant)}
                  onMouseEnter={() => setHoveredId(restaurant.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onKeyDown={(e) => handleKeyDown(e, restaurant)}
                  tabIndex={0}
                  aria-label={`${t("select")} ${restaurant.name || restaurant.id}`}
                  aria-pressed={isSelected}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
                    border: isSelected
                      ? `2px solid ${theme.colors.primary.main}`
                      : `2px solid ${theme.colors.neutral.gray300}`,
                    borderRadius: theme.borderRadius.xl,
                    background: isSelected
                      ? theme.colors.primary.lightest
                      : isHovered
                        ? theme.colors.neutral.gray100
                        : theme.colors.neutral.white,
                    cursor: "pointer",
                    transition: `all ${theme.transitions.fast}`,
                    boxShadow: isHovered || isSelected ? theme.shadows.md : theme.shadows.sm,
                    transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                    outline: "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.md }}>
                    <ShopOutlined
                      style={{
                        fontSize: theme.typography.fontSize["2xl"],
                        color: isSelected ? theme.colors.primary.main : theme.colors.text.secondary,
                      }}
                    />
                    <div style={{ textAlign: "left" }}>
                      <div
                        style={{
                          fontSize: theme.typography.fontSize.lg,
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: theme.colors.text.primary,
                          marginBottom: "2px",
                        }}
                      >
                        {restaurant.name || restaurant.id}
                      </div>
                      {restaurant.roles && restaurant.roles.length > 0 && (
                        <div
                          style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.colors.text.secondary,
                          }}
                        >
                          {restaurant.roles.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>

                  {isSelected && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: theme.borderRadius.full,
                        background: theme.colors.primary.main,
                        color: theme.colors.text.inverse,
                      }}
                    >
                      <CheckOutlined style={{ fontSize: theme.typography.fontSize.base }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ textAlign: "center", marginTop: theme.spacing.xl }}>
            <button
              onClick={handleLogout}
              style={{
                background: "none",
                border: "none",
                color: theme.colors.text.secondary,
                fontSize: theme.typography.fontSize.sm,
                cursor: "pointer",
                textDecoration: "underline",
                padding: theme.spacing.sm,
              }}
              aria-label={t("tooltip_logout")}
            >
              {t("tooltip_logout")}
            </button>
          </div>
        </Card>

        <div style={{ textAlign: "center", marginTop: token.marginXL }}>
          <Text
            style={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: token.fontSizeSM,
            }}
          >
            © {new Date().getFullYear()} TicketRails. All rights reserved.
          </Text>
        </div>
      </div>
    </PublicLayout>
  );
};

export default RestaurantSelection;
